import React, { useRef } from 'react'
//material ui
import { makeStyles, Container, Box, Button } from '@material-ui/core'
import { KeyboardArrowLeft, KeyboardArrowRight, Room, ConfirmationNumber, Event as EventIcon } from '@material-ui/icons'
//React Slick Carousel
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
//animation
import Fade from 'react-reveal/Fade';

/**
 * Eventtemplate
 * -------------------------------------------------------
 * Events / Conferences section, built to match the same
 * pattern already used in Blogs.js (react-slick + custom
 * KeyboardArrowLeft/Right nav buttons + react-reveal Fade +
 * the site's blue/red palette).
 *
 * Every slide uses the SAME layout — image on the left,
 * content on the right — whether there's 1 event or many.
 * With only one event, the arrow buttons simply don't render
 * and autoplay/looping is switched off, so nothing looks broken.
 *
 * HOW TO USE:
 * 1. Copy this file into src/components/templates/HomepageTemplate/EventsConferencesSection/index.js
 *    (same folder depth as Blogs, Reshape, Portfolio, Expanding, TechCarousel).
 * 2. Replace the `events` array below with your real data.
 * 3. It's already imported in HomepageTemplate/index.js as:
 *      import { Eventtemplate } from './EventsConferencesSection'
 *      <Eventtemplate />
 * 4. Swap the placeholder images for real photos whenever you have them.
 * `boothNumber` only needs to be set when tag === "Exhibitor".
 * `brochure` is optional — set it to a PDF link (or any URL) and a
 * "Download Brochure" button appears on that event's slide. Leave it
 * out and no button shows.
 */

const useStyles = makeStyles((theme) => ({
    section: {
        margin: '0% auto',
        marginTop: '8%',
        marginBottom: '8%',
        [theme.breakpoints.down('sm')]: {
            marginTop: '15%',
            marginBottom: '10%',
        },
    },
    heading: {
        textAlign: 'center',
        marginBottom: '4%',
    },
    eyebrow: {
        letterSpacing: 2,
        textTransform: 'uppercase',
        fontWeight: 'bold',
        color: '#182AC3',
        fontSize: '.85rem',
        marginBottom: '1%',
    },
    title: {
        fontWeight: 'bold',
        color: 'black',
        marginBottom: '1%',
    },
    subtitle: {
        color: 'black',
        opacity: 0.7,
        maxWidth: '640px',
        margin: '0 auto',
    },
    sliderSec: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '3%',
    },
    slider: {
        width: '100%',
    },
    btn: {
        width: '50px',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #182AC3',
        cursor: 'pointer',
        color: '#182AC3',
        flexShrink: 0,
        "&:hover": {
            background: '#182AC3',
            color: 'white'
        },
        [theme.breakpoints.down('sm')]: {
            width: '40px',
            height: '40px',
        },
    },
    slide: {
        boxShadow: '0px 0px 20px #0000001F',
        borderBottom: '3px solid #182AC3',
        background: 'white',
        margin: '1% 1rem',
        display: 'flex',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            margin: '0% 20px'
        }
    },
    imageBox: {
        flex: '0 0 45%',
        overflow: 'hidden',
        [theme.breakpoints.down('sm')]: {
            flex: '0 0 auto',
            height: '55vw',
        },
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    contentBox: {
        flex: '1',
        padding: '4% 5%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    smallBtn: {
        font: 'normal normal bold .7rem Access',
        background: '#182AC3',
        border: 'none',
        color: 'white',
        padding: '7px 15px',
        alignSelf: 'flex-start',
        marginBottom: '4%',
        [theme.breakpoints.down('sm')]: {
            fontSize: '.6rem',
            padding: '5px 15px',
        },
        '&:hover': {
            background: '#182AC3',
            color: 'white',
        }
    },
    exhibitorBtn: {
        background: '#DE141A',
    },
    brochureBtn: {
        border: '1px solid #182AC3',
        color: '#182AC3',
        background: 'transparent',
        font: 'normal normal bold .8rem Access',
        padding: '7px 18px',
        borderRadius: '3px',
        textTransform: 'none',
        marginTop: '5%',
        alignSelf: 'flex-start',
        "&:hover": {
            background: '#182AC3',
            color: 'white',
        },
    },
    cardHead: {
        color: 'black',
        marginBottom: '4%',
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        color: 'black',
        opacity: 0.75,
        fontSize: '.95rem',
        marginBottom: '2%',
        '& svg': {
            fontSize: '1.1rem',
            marginRight: '8px',
        }
    },
}))

// Replace with your actual event(s). Add as many as you like — the layout
// and arrow logic adapt automatically.
const events = [
    {
        name: "Semicon India",
        date: "2025",
        location: "New Delhi, India",
        tag: "Exhibitor",
        boothNumber: "B-142",
        image: "https://placehold.co/700x500?text=Semicon+India",
        brochure: "https://example.com/brochures/semicon-india.pdf",
    },
    {
        name: "Electronica India",
        date: "2025",
        location: "Bengaluru, India",
        tag: "Conference",
        image: "https://placehold.co/700x500?text=Electronica+India",
    },
    {
        name: "IoT Tech Expo",
        date: "2025",
        location: "Gandhinagar, India",
        tag: "Speaker",
        image: "https://placehold.co/700x500?text=IoT+Tech+Expo",
    },
];

export function Eventtemplate() {
    const classes = useStyles()
    const sliderRef = useRef();
    const hasMultiple = events.length > 1;

    const next = () => {
        sliderRef.current.slickNext();
    };
    const previous = () => {
        sliderRef.current.slickPrev();
    };

    const settings = {
        arrows: false,
        infinite: hasMultiple,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: hasMultiple,
        autoplaySpeed: 5000,
        adaptiveHeight: true,
    };

    return (
        <Container fixed className={classes.section}>
            <Box className={classes.heading}>
                <h6 className={classes.eyebrow}>Where you'll find us</h6>
                <h2 className={classes.title}>Events &amp; Conferences</h2>
                <h6 className={classes.subtitle}>
                    SLS regularly participates in leading industry events, exhibitions
                    and conferences — here's where we've shown up recently.
                </h6>
            </Box>

            <Box className={classes.sliderSec}>
                {hasMultiple && (
                    <Box><Fade left><KeyboardArrowLeft className={classes.btn} onClick={previous} /></Fade></Box>
                )}

                <Slider ref={sliderRef} {...settings} className={classes.slider}>
                    {events.map((event) => (
                        <Box key={event.name}>
                            <Box className={classes.slide} data-aos="fade-up">
                                <Box className={classes.imageBox}>
                                    <img className={classes.image} src={event.image} alt={event.name} />
                                </Box>
                                <Box className={classes.contentBox}>
                                    <Fade bottom>
                                        <Box>
                                            {event.tag && (
                                                <Button
                                                    className={clsxTag(classes, event.tag)}
                                                >
                                                    {event.tag}
                                                </Button>
                                            )}
                                            <h4 className={classes.cardHead}>{event.name}</h4>
                                            <Box className={classes.metaRow}>
                                                <EventIcon />
                                                <span>{event.date}</span>
                                            </Box>
                                            <Box className={classes.metaRow}>
                                                <Room />
                                                <span>{event.location}</span>
                                            </Box>
                                            {event.tag === "Exhibitor" && event.boothNumber && (
                                                <Box className={classes.metaRow}>
                                                    <ConfirmationNumber />
                                                    <span>Booth {event.boothNumber}</span>
                                                </Box>
                                            )}
                                            {event.brochure && (
                                                <Button
                                                    className={classes.brochureBtn}
                                                    href={event.brochure}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Download Brochure
                                                </Button>
                                            )}
                                        </Box>
                                    </Fade>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Slider>

                {hasMultiple && (
                    <Box><Fade right><KeyboardArrowRight className={classes.btn} onClick={next} /></Fade></Box>
                )}
            </Box>
        </Container>
    )
}

// small helper to combine the base smallBtn class with the red variant for Exhibitor
function clsxTag(classes, tag) {
    return tag === "Exhibitor"
        ? `${classes.smallBtn} ${classes.exhibitorBtn}`
        : classes.smallBtn;
}